import axios from 'axios'

import {
  // cacheAdapterEnhancer,
  throttleAdapterEnhancer,
} from 'axios-extensions'

const LogRocket = window.LogRocket

const state = {
  http: axios.create({
    baseURL: '/',
    headers: {
      'Cache-Control': 'no-cache',
    },
    adapter: throttleAdapterEnhancer(axios.defaults.adapter, {
      thredshold: 2 * 1000,
    })
  }),
}

const getters = {
  // Another common content-type would be 'application/json' which can be passed as a config to each api action below
  config(state, getters, rootState) {
    return {
      baseURL: '/',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        'Cache-Control': 'no-cache',
      },
    }
  },
}

const actions = {
  cachedGet({ getters, dispatch, state }, { url, config = getters.config }) {
    return new Promise((resolve, reject) => {
      state.http
        .get(url, {
          useCache: true,
          ...config,
        })
        .then((response) => resolve(response.data))
        .catch((error) => {
          dispatch('error', {
            error,
            url,
          })
          reject(error)
        })
    })
  },
  get({ getters, dispatch }, { url, config = getters.config, cancelToken }) {
    if (cancelToken) {
      config = {
        ...config,
        cancelToken: cancelToken,
      }
    }

    return new Promise((resolve, reject) => {
      // append timestamp to url
      if (!url.includes('?')) {
        url += '?ts=' + new Date().getTime()
      } else {
        url += '&ts=' + new Date().getTime()
      }
      axios
        .get(url, config)
        .then((response) => resolve(response.data))
        .catch((errorResponse) => {
          let error = errorResponse
          if (typeof error === 'string') {
            error = {
              message: errorResponse,
            }
          }
          reject(error)
          dispatch('error', {
            error,
            url,
          })
        })
    })
  },
  post({ getters, dispatch }, { url, data = {}, config = getters.config }) {
    return new Promise((resolve, reject) => {
      // append timestamp to url
      axios
        .post(url, data, config)
        .then((response) => resolve(response.data))
        .catch((error) => {
          dispatch('error', {
            error,
            errorUrl: url,
          })
          reject(error)
        })
    })
  },
  error({ dispatch }, { error, errorUrl }) {
    const errorMsg = error.message || error
    const errorStatus = error.response ? error.response.status || '' : null

    dispatch('logging/captureException', { error: errorMsg, errorExtra: error }, { root: true })

    if (error.response && error.response.status && (error.response.status === 403 || error.response.status === 401)) {
      window.location = '/login'
    }
  },
}

// Add an interceptor to apply reCAPTCHA to requests
const reCaptchaPublicKey = window.storeSettings?.CaptchaSettings?.SiteKey;
const isReCaptchaEnabled = window.storeSettings?.CaptchaSettings?.IsEnabled;

// Define the URLs and methods that require reCAPTCHA validation
export const reCaptchaUrlActions = [
  { url: 'api/resortcharge/updatewaivercreditcard', method: 'POST', recaptchaAction: 'resortcharge' },
  { url: '/media', method: 'POST', recaptchaAction: 'fastflow' },
  { url: 'api/checkout/guestinfo/add', method: 'POST', recaptchaAction: 'myaccount/addgroupmember' },
  { url: 'api/vouchers/validate', method: 'POST', recaptchaAction: 'vouchers' },
  { url: 'api/checkout/personalization', method: 'GET', recaptchaAction: 'checkout/personalization' },
  { url: 'api/checkout/complete', method: 'POST', recaptchaAction: 'checkout/complete' },
];

const executeReCaptcha = async (action) => {
  if(reCaptchaPublicKey){
    return new Promise((resolve, reject) => {
      grecaptcha.ready(() => {
        grecaptcha.execute(reCaptchaPublicKey, { action }).then(resolve).catch(reject);
      });
    });
  }
};

// Apply reCAPTCHA to the request
const applyReCaptchaToRequest = (config) => {
  const matchingAction = reCaptchaUrlActions.find(item =>
    config.url.includes(item.url) && config.method.toUpperCase() === item.method.toUpperCase()
  );

  if (matchingAction) {
    return executeReCaptcha(matchingAction.recaptchaAction).then(recaptchaToken => {
      if (recaptchaToken) {
        config.headers['Recaptcha-Token'] = recaptchaToken;
        config.headers['Recaptcha-Action'] = matchingAction.recaptchaAction;
      }
      return config;
    }).catch(error => {
      console.log('Error executing reCAPTCHA:', error);
      return Promise.reject(error);
    });
  }

  // Resolve with the original config if no matching action
  return Promise.resolve(config);
}

axios.interceptors.request.use(config => {
  // Check if the request requires reCAPTCHA validation
  if (isReCaptchaEnabled && reCaptchaUrlActions.some(item => config.url.includes(item.url) && config.method.toUpperCase() === item.method.toUpperCase())) {
    return applyReCaptchaToRequest(config);
  }
  return config;  // Return unmodified config for other requests
}, error => Promise.reject(error));

export default {
  namespaced: true,
  state,
  getters,
  actions,
}
